import * as DateF from 'date-fns'
import { useMemo } from 'react'
import styled from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { PairSnipersBundlersInsidersTooltipBody } from '@root/features/explore/ui/pair-explore-item/audit/pair-snipers-bundlers-insiders-tooltip-body/pair-snipers-bundlers-insiders-tooltip-body'

import type { HoldersCharts } from '@root/entities/holders-charts/holders-charts'
import type { ClassNameProps } from '@root/shared/react'

export type TradeHoldersChartTooltipProps = {
  date: number | undefined
  dateFormatted: string | undefined
  data: HoldersCharts
} & ClassNameProps

const order = ['insiders', 'bundlers', 'snipers'] as const

// NOTE: This component is mounted on it's own root element, so no contexts will be used
export const TradeHoldersChartTooltip: React.FC<
  TradeHoldersChartTooltipProps
> = ({ className, date, dateFormatted, data }) => {
  const second = useMemo(
    () => (date ? DateF.startOfSecond(date) : undefined),
    [date],
  )
  if (!date || !second) {
    return undefined
  }

  const bundlers = data.bundlersHoldingChart.find((it) =>
    DateF.isSameSecond(it.date, second),
  )

  const snipers = data.snipersHoldingChart.find((it) =>
    DateF.isSameSecond(it.date, second),
  )

  const insiders = data.insidersHoldingChart.find((it) =>
    DateF.isSameSecond(it.date, second),
  )

  return (
    <Root className={className}>
      <Header>
        <Title>{dateFormatted}</Title>
      </Header>
      <PairSnipersBundlersInsidersTooltipBody
        bundlersHolding={bundlers?.value}
        snipersHolding={snipers?.value}
        insidersHolding={insiders?.value}
        order={order}
      />
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  gap: 8px;
  padding: 10px;

  border-radius: 8px;
  border: 1px solid var(--backgroundA-3);
  background: var(--background-4);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.65);
`

const Header = styled.div`
  padding-bottom: 8px;
  border-bottom: 1px solid var(--backgroundA-4);
`

const Title = styled(Typography).attrs({
  variant: 'body4-semi-bold',
})`
  color: var(--text-11);
`
